import { Link } from "react-router-dom";
import { MapPin } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import CallButton from "../components/CallButton";
import TrustBar from "../components/TrustBar";
import FinalCTABanner from "../components/FinalCTABanner";
import StaticMap from "../components/StaticMap";
import { IMAGES } from "../data/constants";
import { CITIES } from "../data/cities";

export default function About() {
  return (
    <>
      <SEO
        title="About GTA Garage Doors | Local GTA Technicians"
        description="Licensed, insured, and local. GTA Garage Doors serves Toronto, Vaughan, Mississauga and beyond. Meet the team that shows up when it matters."
        canonical="/about"
        ogImage={IMAGES.heroTechnician}
      />

      <section className="relative bg-[#1B2A4A] text-white" data-testid="about-hero">
        <img
          src={IMAGES.heroTechnician}
          alt="GTA Garage Doors licensed technician working on a residential garage door"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A] via-[#1B2A4A]/85 to-[#1B2A4A]/40" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24">
          <nav className="text-xs text-white/60 font-bold uppercase tracking-widest font-display">
            <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">About</span>
          </nav>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">
            Local technicians. Real expertise. <span className="text-[#FF6B00]">No games.</span>
          </h1>
          <div className="mt-8">
            <CallButton size="lg" testId="about-hero-call-button" />
          </div>
        </div>
      </section>

      <TrustBar variant="dark" />

      <section className="section-pad bg-white">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow">Our story</div>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight text-[#1B2A4A]">
              Built for GTA homeowners who actually need help.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#1B2A4A] leading-relaxed">
              GTA Garage Doors & Locksmith was built for one reason: GTA
              homeowners deserve a service company that actually shows up — fast
              and professional.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#1B2A4A] leading-relaxed">
              Our licensed technicians live and work in the communities we serve.
              When you call us at 2am because your door was broken into, a real
              tech answers and a real tech shows up. That's the promise.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#1B2A4A] leading-relaxed">
              We don't run bait-and-switch pricing. We don't charge
              "after-hours" premiums. We don't send an unlicensed contractor
              pretending to be a locksmith. We run a proper business with
              trucks, parts, licensed people, and a phone number that's
              answered day or night.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-sm overflow-hidden border border-[#E2E8F0]">
              <img
                src={IMAGES.heroTechnician}
                alt="GTA Garage Doors licensed technician on a residential service call"
                className="w-full h-72 md:h-80 object-cover"
                loading="lazy"
              />
            </div>
            <div className="mt-6 rounded-sm bg-[#FF6B00] text-white p-6">
              <div className="eyebrow !text-white/80">By the numbers</div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div>
                  <div className="font-display font-black text-3xl">12</div>
                  <div className="text-xs font-bold uppercase tracking-widest mt-1 text-white/90">Cities served</div>
                </div>
                <div>
                  <div className="font-display font-black text-3xl">4.9</div>
                  <div className="text-xs font-bold uppercase tracking-widest mt-1 text-white/90">Avg rating</div>
                </div>
                <div>
                  <div className="font-display font-black text-3xl">24/7</div>
                  <div className="text-xs font-bold uppercase tracking-widest mt-1 text-white/90">On call</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="section-pad bg-white" data-testid="about-service-area">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="eyebrow">Service area</div>
              <h2 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight text-[#1B2A4A]">
                12 cities. One team. Always nearby.
              </h2>
            </div>
            <Link
              to="/areas"
              data-testid="about-view-areas-link"
              className="font-bold font-display text-[#FF6B00] uppercase tracking-widest text-sm hover:underline"
            >
              View all areas →
            </Link>
          </div>
          <div className="mt-8">
            <StaticMap city="GTA Coverage" testId="about-map" />
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/areas/${c.slug}`}
                data-testid={`about-city-${c.slug}`}
                className="rounded-sm border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-bold font-display text-[#1B2A4A] hover:border-[#FF6B00] hover:text-[#FF6B00] text-center transition-all inline-flex items-center justify-center gap-1.5"
              >
                <MapPin size={12} weight="fill" className="text-[#FF6B00]"/>
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTABanner />
    </>
  );
}
